import type { Exposure } from "@amplitude/experiment-js-client"
import type {
  MetadataType,
  OnboardingTrackEvent,
} from "@prismicio/editor-fields"
import { request } from "@prismicio/editor-support/Http"
import { captureException } from "@sentry/react"
import { unknown } from "zod"

import type { OnboardingStep } from "../../onboarding/OnboardingSteps"
import { env } from "../env"
import { getRepositoryName } from "../routing"
import { baseUrl } from "./core"

async function trackEvent(name: string, traits: Record<string, string>) {
  if (env.PRISMIC_DEMO) return

  const body = {
    name,
    source: "Page Builder",
    traits,
    groupId: getRepositoryName(),
  }

  return request(new URL("tracking/trace", baseUrl), {
    method: "POST",
    body,
    keepalive: true,
    okType: unknown(),
  }).catch(captureException)
}

export function trackExperimentExposure(exposure: Exposure) {
  const { metadata: _, ...traits } = exposure
  void trackEvent("$exposure", traits)
}

export function trackEditorEvent(
  event: string,
  args: Record<string, string> = {},
) {
  void trackEvent(event, args)
}

export type SliceTrackingArgs = {
  id: string
  variation: string
  zone: string
  customtypeId: string
  sliceName: string
  documentName: string
  type: "shared" | "composite" | "simple"
}

type SliceAddedArgs = SliceTrackingArgs & {
  promptId?: string
}

export function trackAddSlice(args: SliceAddedArgs) {
  void trackEvent("Slice Added", args)
}

export function trackDeleteSlice(args: SliceTrackingArgs) {
  void trackEvent("Slice Deleted", args)
}

type SliceCopyPasteArgs = SliceTrackingArgs & {
  keyboard: boolean
}

export function trackCopyPasteSlice(
  action: "copy" | "paste",
  args: SliceCopyPasteArgs,
) {
  const { keyboard } = args
  const eventName = action === "copy" ? "Slice Copied" : "Slice Pasted"
  void trackEvent(eventName, { ...args, keyboard: `${keyboard}` })
}

type SliceVariationChangedArgs = SliceTrackingArgs & {
  variationName: string
}

export function trackSliceVariationChanged(args: SliceVariationChangedArgs) {
  void trackEvent("Slice Variation Changed", args)
}

export function trackSimulatorUrlSetup(args: { url: string }) {
  void trackEvent("Simulator URL Setup", args)
}

export function trackPreviewOpen(args: { documentId: string; label: string }) {
  void trackEvent("Open Preview", args)
}

export function trackSlicePromptedStarted(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Slice Prompted Started", args)
}

export function trackSlicePromptedDone(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  slices: string
  cost: string
  time: string
}) {
  void trackEvent("Slice Prompted Done", args)
}

export function trackRefineSliceStarted(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
}) {
  void trackEvent("Refine Slice Started", args)
}

export function trackRefineSliceDone(args: {
  customTypeId: string
  documentId?: string
  prompt: string
  promptId: string
  locale: string
  cost: string
  time: string
}) {
  void trackEvent("Refine Slice Done", args)
}

export function trackSmartCopyPasteStarted(args: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
}) {
  void trackEvent("Smart Copy Paste Started", args)
}

export function trackSmartCopyPasteDone(args: {
  customTypeId: string
  documentId?: string
  sliceId: string
  variationId: string
  retried?: "true"
  cost: string
  time: string
  distance: string
}) {
  void trackEvent("Smart Copy Paste Done", args)
}

export function trackTranslateDocumentStarted(args: {
  customTypeId: string
  documentId?: string
  sourceLocale: string
  targetLocale: string
  promptId: string
}) {
  void trackEvent("Translate Document Started", args)
}

export function trackTranslateDocumentDone(args: {
  customTypeId: string
  documentId?: string
  sourceLocale: string
  targetLocale: string
  promptId: string
  model: string
  sourceTokens: string
  targetTokens: string
  tokenRatio: string
  cost: string
  time: string
}) {
  void trackEvent("Translate Document Done", args)
}

export function trackDocumentCopiedToLocale(args: {
  customTypeId: string
  documentId?: string
  languageFrom: string
  languageTo: string
}) {
  void trackEvent("Document Copied To Locale", args)
}

export function trackLivePreviewModalOpen(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Live Preview Modal Open", args)
}

export function trackWhatsNewModalOpen(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
}) {
  void trackEvent("Whats New Modal Open", args)
}

export function trackGlobalErrorScreenDisplayed(error: unknown) {
  const args =
    error instanceof Error
      ? { error: error.name, description: error.message }
      : { error: "Unknown", description: error?.toString() ?? "Not available" }

  void trackEvent("Global Error Screen Displayed", args)
}

export function trackDocumentWithLiveEditingPreview(args: {
  documentId: string
  customTypeId: string
  repositoryId: string
  preview: "true" | "false"
  error?: string
}) {
  void trackEvent("Document With Live Editing Preview", args)
}

/**
 * Tracks a document search event in dialog.
 *
 * @param searchQuery - The term user searched for.
 * @param numberOfWords - The number of words term had.
 * @param numberOfResults - The number of results that were returned for this search (excluding archived documents).
 * @param timeToGetResultsInMs - The number of milliseconds that it took to perform the search request.
 */
export function trackDocumentSearchInDialog(params: {
  searchQuery: string
  numberOfWords: number
  numberOfResults: number
  timeToGetResultsInMs: number
}) {
  void trackEvent("New Doc List Search", {
    searchQuery: params.searchQuery,
    numberOfWords: params.numberOfWords.toString(),
    numberOfResults: params.numberOfResults.toString(),
    timeToGetResultsInMs: params.timeToGetResultsInMs.toString(),
  })
}

/**
 * Tracks a document selection event in the search dialog.
 *
 * @param searchQuery - The term user searched for.
 * @param numberOfWords - The number of words term had.
 * @param numberOfResults - The number of results that were returned for this search (excluding archived documents).
 * @param searchPosition - The position of the selected document in the result set.
 */
export function trackSelectDocumentInDialog(params: {
  searchQuery: string
  numberOfWords: number
  numberOfResults: number
  searchPosition: number
}) {
  void trackEvent("Page Selected", {
    searchQuery: params.searchQuery,
    numberOfWords: params.numberOfWords.toString(),
    numberOfResults: params.numberOfResults.toString(),
    searchPosition: params.searchPosition.toString(),
  })
}

const eventNameMap: Record<OnboardingTrackEvent["event"], string> = {
  "shared-onboarding:step-completed": "Prismic Onboarding Guide Step Completed",
  "shared-onboarding:step-opened": "Prismic Onboarding Guide Step Open",
  "shared-onboarding:completed": "Prismic Onboarding Guide Completed",
  "shared-onboarding:follow-tutorial":
    "Prismic Onboarding Guide Follow Tutorial",
}

export function trackSharedOnboardingGuide(event: OnboardingTrackEvent) {
  const eventName = eventNameMap[event.event]
  void trackEvent(eventName, event)
}

export type SeoSuggestionPayloadTracking = {
  documentId: string
  locale: string
}

export function trackSeoModalOpen(args: SeoSuggestionPayloadTracking) {
  void trackEvent("SEO Metadata Modal Open", args)
}

type SeoFieldTracking = {
  edited: boolean
  lengthChange: number
  characterOverlap: number
}

export function trackApplyMetadataSuggestion(
  args: {
    title?: SeoFieldTracking
    description?: SeoFieldTracking
  } & SeoSuggestionPayloadTracking,
) {
  const { title: titleField, description: descriptionField, ...rest } = args

  const createPayload = (
    field: SeoFieldTracking | undefined,
    fieldName: "title" | "description",
  ) => {
    if (!field) return {}
    return {
      [`${fieldName}Edited`]: field.edited.toString(),
      [`${fieldName}LengthChange`]: field.lengthChange.toString(),
      [`${fieldName}CharacterOverlap`]: field.characterOverlap.toString(),
    }
  }

  const titlePayload = createPayload(titleField, "title")
  const descriptionPayload = createPayload(descriptionField, "description")

  void trackEvent("Apply metadata suggestion", {
    ...rest,
    ...titlePayload,
    ...descriptionPayload,
  })
}

export function trackDiscardMetadataSuggestion(
  args: SeoSuggestionPayloadTracking,
) {
  void trackEvent("Discard metadata suggestion", args)
}

export function trackMetadataGeneration(
  args: {
    suggestion: string
    type: MetadataType
  } & SeoSuggestionPayloadTracking,
) {
  void trackEvent("Generate metadata", args)
}

type TeamSpaceCreatedArgs = {
  name: string
  customTypes: string[]
}

export function trackTeamSpaceCreated(args: TeamSpaceCreatedArgs) {
  void trackEvent("Team Space Created", {
    name: args.name,
    customTypes: args.customTypes.join(","),
    numberOfCustomTypes: args.customTypes.length.toString(),
  })
}

type TeamSpaceUpdatedArgs = {
  old: TeamSpaceCreatedArgs
  new: TeamSpaceCreatedArgs
}

export function trackTeamSpaceUpdated(args: TeamSpaceUpdatedArgs) {
  void trackEvent("Team Space Updated", {
    oldName: args.old.name,
    oldCustomTypes: args.old.customTypes.join(","),
    oldNumberOfCustomTypes: args.old.customTypes.length.toString(),
    name: args.new.name,
    customTypes: args.new.customTypes.join(","),
    numberOfCustomTypes: args.new.customTypes.length.toString(),
  })
}

type TeamSpaceDeletedArgs = {
  name: string
  customTypes: string[]
}

export function trackTeamSpaceDeleted(args: TeamSpaceDeletedArgs) {
  void trackEvent("Team Space Deleted", {
    name: args.name,
    customTypes: args.customTypes.join(","),
    numberOfCustomTypes: args.customTypes.length.toString(),
  })
}

export function trackPbOnboardingStep(stepName: OnboardingStep) {
  void trackEvent("Page Builder Onboarding Step Passed", { stepName })
}
